/**
 * Which "What's new" release the user has seen last (MEIS-13668) — the one
 * source of truth the pane's "New" markers, the pane's header-button badge and
 * the launcher's news dot all read, so they change together.
 *
 * The newest seen release id is remembered per browser in localStorage under
 * the key the MEIS-13490 prototype already used. localStorage written in the
 * same tab does not notify that tab, so subscribers learn about a write
 * through a module-local listener set read via `useSyncExternalStore` (same
 * pattern as `ItemDetailHintBar`). Storage may be blocked: a failed read means
 * "nothing seen", and a failed write still keeps the value in memory for this
 * tab, so the dot and badge clear even though the next visit counts the
 * newest release as new again.
 */
import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  useVisibleChangelogReleases,
  type ChangelogOptions,
} from "./agentChangelogReleases.js";

const SEEN_KEY = "msys-agent-changelog-seen";

/** Set by a write in this tab; wins over storage, which may have refused it. `undefined` = no write yet. */
let writtenId: string | null | undefined;

const listeners = new Set<() => void>();

export function subscribeToSeenRelease(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function readSeenReleaseId(): string | null {
  if (writtenId !== undefined) return writtenId;
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markReleaseSeen(id: string): void {
  writtenId = id;
  try {
    window.localStorage.setItem(SEEN_KEY, id);
  } catch {
    // Storage blocked: the in-memory value above still serves this tab.
  }
  listeners.forEach(listener => listener());
}

/** Test seam: forgets this tab's in-memory value so the next read goes to storage. */
export function resetAgentChangelogSeenForTests(): void {
  writtenId = undefined;
}

/**
 * The releases the user has not seen: those listed before `seenId` (newest
 * first). Someone who never opened the pane, or whose seen release is no
 * longer listed, sees only the newest one as new, never the whole history.
 */
export function unseenReleaseIds(
  releaseIds: readonly string[],
  seenId: string | null
): string[] {
  const seenIndex = seenId ? releaseIds.indexOf(seenId) : -1;
  return seenIndex === -1
    ? releaseIds.slice(0, 1)
    : releaseIds.slice(0, seenIndex);
}

/**
 * The unseen releases among the *visible* ones (see
 * `useVisibleChangelogReleases`), whether there are any, and a way to mark a
 * release as seen. On a query error or refusal nothing is visible, so there
 * is no unseen release. `{ enabled: false }` (no assistant access) skips the
 * query, so `hasUnseen` is false.
 */
export function useAgentChangelogUnseen(options: ChangelogOptions = {}) {
  const releases = useVisibleChangelogReleases(options);
  const seenId = useSyncExternalStore(
    subscribeToSeenRelease,
    readSeenReleaseId,
    () => null
  );
  const unseenIds = useMemo(
    () =>
      unseenReleaseIds(
        (releases ?? []).map(release => release.id),
        seenId
      ),
    [releases, seenId]
  );
  const markSeen = useCallback((id: string) => markReleaseSeen(id), []);
  return { unseenIds, hasUnseen: unseenIds.length > 0, markSeen };
}
